/*
 * MEPP - A web application to guide patients and clinicians in the process of
 * facial palsy rehabilitation, with the help of the mirror effect and principles
 * of motor learning
 * Copyright (C) 2021 MEPP <info@mirroreffectplus.org>
 *
 * This file is part of MEPP.
 *
 * MEPP is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * MEPP is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with MEPP.  If not, see <http://www.gnu.org/licenses/>.
 */

export default {
  home: {
    intro: {
      title:
        'A Crucial Innovation for Facial Rehabilitation Based on Augmented Reality',
      text: 'The MEPP is an innovative tool for facial rehabilitation, aimed at treating facial paralysis. Born from collaborative research in Quebec, it brings together researchers, patients, clinicians, technologists, and students to improve the quality of life for people with facial paralysis. The MEPP uses augmented reality and is based on evidence to make facial rehabilitation more accessible.',
      financialTitle: 'With the financial support of:',
    },
    description: {
      title: 'Mirror therapy in facial rehabilitation',
      text: 'The MEPP comes with an application for mobile devices. It allows a therapist to provide mirror therapy in facial rehabilitation for patients suffering from facial paralysis. The MEPP offers a platform for clinicians to manage a database of exercises, produce individualized exercise plans, and add multiple users. In addition, the site provides a personalized virtual space allowing individuals in need of rehabilitation to perform their exercises.',
      list: {
        title:
          'Several studies have been conducted to test the usability and effectiveness of the MEPP:',
        loading: 'Loading in progress…',
        error: 'An error occurred during loading!',
      },
    },
    history: {
      title:
        'Approximately <span style="color:#008EDE;">15.4 million</span> people worldwide each year will experience facial paralysis caused by Bell’s palsy or a stroke.',
      text1:
        'Approximately 15.4 million (M) people worldwide each year will experience facial paralysis due to Bell’s palsy or a stroke (Feigin et al, 2022). This represents ≈110,000 and ≈23,000 people each year in Canada and Quebec, respectively. For these individuals, rehabilitation is therefore essential to promote the recovery of functions and to improve their quality of life. Rehabilitation is carried out through the joint efforts of patients and health professionals specialized in the assessment and rehabilitation of facial paralysis. However, for these professionals, very few specialized and evidence-based tools are currently available. They must primarily work by exploring, tentatively, a vast and disparate literature, which hinders the effectiveness of their interventions and may lead to poorer clinical outcomes.',
      text2:
        'In order to address this important issue, the research team composed of Dr. Sarah Martineau, speech-language pathologist and assistant clinical professor at the Université de Montréal, Dr. Karine Marcotte, speech-language pathologist and researcher, and Dr. Akram Rahal, ENT specialist and clinical professor at the Université de Montréal, developed the Mirror Effect Plus Protocol (MEPP) between 2017 and 2023. This rehabilitation, along with the accompanying website, is based on important intervention principles in facial paralysis, as well as on mirror therapy. The site integrates a progression of exercises and visual aids to facilitate the rehabilitation of individuals with neurological impairments of the face. The MEPP offers a personalized approach to facial rehabilitation, unique compared to the available facial rehabilitation software on the market. Furthermore, the rehabilitation protocols and exercises integrated into the site are based on the latest evidence-based data. This tool greatly facilitates the work of professionals, as well as the therapeutic process for patients, since it offers a clear structure based on scientific literature, while also facilitating access to home exercises and tele-rehabilitation in the field of facial paralysis.',
      persons: {
        person1: {
          name: 'Dr. Sarah Martineau',
          title:
            'Speech-language pathologist and assistant clinical professor at the University of Montreal',
        },
        person2: {
          name: 'Dr. Karine Marcotte',
          title: 'Speech therapist and researcher',
        },
        person3: {
          name: 'Dr Akram Rahal',
          title: 'ENT and clinical professor at the University of Montreal',
        },
      },
    },
    donate: {
      title: 'Help us make the MEPP accessible to everyone.',
      text: 'All donations directly support the development of the MEPP so that we can provide free access to our tools to those who need them the most. Make a donation today.',
    },
  },
};
